// The yearly subscription, invoices and, only after the grace period, the switch.
// Agreed: reminders to our team only (60, 30 and 7 days before), 30 days of grace,
// then we switch the site off by hand once head office has had a final warning.

import { useState, type FormEvent } from 'react';
import { api, message } from '../../api.ts';
import { day } from '../../format.ts';
import type { InvoiceRow, SiteView } from '../../types.ts';
import { Dialog, Loading, Problem, useLoad } from '../../ui.tsx';

const toB64u = (bytes: Uint8Array): string => {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
const today = () => new Date().toISOString().slice(0, 10);
const plusYear = (d: string) => {
  const [y, m, dd] = d.split('-').map(Number);
  return `${y + 1}-${String(m).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
};

export function Subscription({ site, reload }: { site: SiteView; reload: () => void }) {
  const invoices = useLoad(() => api<{ invoices: InvoiceRow[] }>('GET', `/sites/${site.siteId}/invoices`), [site.siteId]);
  const [dialog, setDialog] = useState<null | 'renew' | 'dates' | 'invoice' | 'off'>(null);
  const [error, setError] = useState<string | null>(null);
  const sub = site.subscription;
  const graceOver = sub.kind === 'grace_ended';

  const setStatus = async (id: string, status: string) => {
    try {
      await api('PUT', `/invoices/${id}/status`, { status });
      invoices.reload();
    } catch (e) {
      setError(message(e));
    }
  };
  const switchOn = async () => {
    if (!confirm('Switch Z.A.S. back on at this site? The tablet can send alerts again straight away.')) return;
    try {
      await api('POST', `/sites/${site.siteId}/switch-on`, {});
      reload();
    } catch (e) {
      setError(message(e));
    }
  };

  const tone = sub.kind === 'overdue' || sub.kind === 'grace_ended' || sub.kind === 'switched_off' ? 'red' : sub.kind === 'renewal_due' ? 'amber' : '';
  return (
    <div className="stack" style={{ gap: 20, maxWidth: 1040 }}>
      {error && <Problem text={error} />}
      <div className="grid4">
        <div className="card">
          <span className="card-label">Plan</span>
          <span className="card-value">Yearly</span>
        </div>
        <div className="card">
          <span className="card-label">Started</span>
          <span className="card-value">{site.startedOn ? day(site.startedOn) : 'When it goes live'}</span>
        </div>
        <div className="card">
          <span className="card-label">Renews</span>
          <span className="card-value">{site.renewsOn ? day(site.renewsOn) : '—'}</span>
        </div>
        <div className="card">
          <span className="card-label">Status</span>
          <span className={`card-value ${tone}`}>{site.subscriptionText}</span>
        </div>
      </div>
      <div className="grid2">
        <div className="card">
          <span className="card-label">Renewal reminders</span>
          <span>60, 30 and 7 days before, to us only</span>
        </div>
        <div className="card">
          <span className="card-label">If not renewed</span>
          <span>Everything keeps working for a {site.graceDays}-day grace period</span>
        </div>
      </div>
      {site.renewsOn && (
        <div className="row">
          <button className="btn primary small" onClick={() => setDialog('renew')}>
            Record a renewal
          </button>
          <button className="btn small" onClick={() => setDialog('dates')}>
            Change the dates
          </button>
        </div>
      )}

      <div className="stack" style={{ gap: 10 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span className="card-label">Invoices</span>
          <button className="btn small" onClick={() => setDialog('invoice')}>
            Upload invoice
          </button>
        </div>
        {invoices.loading && !invoices.data && <Loading />}
        {invoices.data && (
          <div className="table">
            <div className="tr th" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
              <span>Invoice</span>
              <span>Date</span>
              <span>Amount</span>
              <span>Status</span>
              <span>File</span>
            </div>
            {invoices.data.invoices.length === 0 && <div className="empty">No invoices yet.</div>}
            {invoices.data.invoices.map((i) => (
              <div key={i.invoiceId} className="tr" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
                <span className="mono">{i.number}</span>
                <span className="muted">{day(i.issuedOn)}</span>
                <span>{i.amount}</span>
                <span>
                  <label className="visually-hidden" htmlFor={`st-${i.invoiceId}`}>
                    Status of {i.number}
                  </label>
                  <select id={`st-${i.invoiceId}`} className="input" style={{ height: 36 }} value={i.status} onChange={(e) => setStatus(i.invoiceId, e.target.value)}>
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                    <option value="void">Void</option>
                  </select>
                </span>
                <a href={`/console-api/invoices/${i.invoiceId}/file`}>PDF</a>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={site.switchedOffAt ? 'card' : 'card'} style={{ borderColor: '#4A2426', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 24 }}>
        {site.switchedOffAt ? (
          <>
            <div className="stack" style={{ gap: 6 }}>
              <span style={{ fontWeight: 500 }}>Z.A.S. is switched off at this site</span>
              <span className="muted small">
                Since {day(site.switchedOffAt)}
                {site.switchedOffBy ? ` · by ${site.switchedOffBy}` : ''}
                {site.offReason ? ` · ${site.offReason}` : ''}. The tablet can't send alerts and every phone says Z.A.S. is off.
              </span>
            </div>
            <button className="btn small" onClick={switchOn}>
              Switch back on
            </button>
          </>
        ) : (
          <>
            <div className="stack" style={{ gap: 6 }}>
              <span style={{ fontWeight: 500 }}>Switch this site off</span>
              <span className="muted small">
                Only once the {site.graceDays}-day grace period has ended and we've given head office a final warning. Then the ops tablet and every phone show that Z.A.S. is switched off, so nobody relies on alerts that won't come.
              </span>
            </div>
            <button className="btn danger small" disabled={!graceOver} title={graceOver ? undefined : 'Only after the grace period ends'} onClick={() => setDialog('off')}>
              Switch site off
            </button>
          </>
        )}
      </div>

      {dialog === 'renew' && site.renewsOn && <RenewDialog site={site} onClose={() => setDialog(null)} onDone={reload} />}
      {dialog === 'dates' && <DatesDialog site={site} onClose={() => setDialog(null)} onDone={reload} />}
      {dialog === 'invoice' && <InvoiceDialog siteId={site.siteId} onClose={() => setDialog(null)} onDone={invoices.reload} />}
      {dialog === 'off' && <SwitchOffDialog site={site} onClose={() => setDialog(null)} onDone={reload} />}
    </div>
  );
}

function useSubmit(onDone: () => void, onClose: () => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = (fn: () => Promise<unknown>) => async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await fn();
      onDone();
      onClose();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

function RenewDialog({ site, onClose, onDone }: { site: SiteView; onClose: () => void; onDone: () => void }) {
  const [renewsOn, setRenewsOn] = useState(plusYear(site.renewsOn as string));
  const { busy, error, run } = useSubmit(onDone, onClose);
  return (
    <Dialog title="Record a renewal" onClose={onClose}>
      <form className="stack" onSubmit={run(() => api('POST', `/sites/${site.siteId}/renewals`, { renewsOn }))}>
        <p className="muted">It currently renews on {day(site.renewsOn)}. Once recorded, the reminders start again for the new date.</p>
        <label className="field">
          Next renewal date
          <input className="input" type="date" value={renewsOn} onChange={(e) => setRenewsOn(e.target.value)} required />
        </label>
        {error && <p className="error">{error}</p>}
        <div className="row">
          <button className="btn primary" type="submit" disabled={busy}>
            Record the renewal
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function DatesDialog({ site, onClose, onDone }: { site: SiteView; onClose: () => void; onDone: () => void }) {
  const [startedOn, setStarted] = useState(site.startedOn ?? today());
  const [renewsOn, setRenews] = useState(site.renewsOn ?? plusYear(today()));
  const { busy, error, run } = useSubmit(onDone, onClose);
  return (
    <Dialog title="Change the subscription dates" onClose={onClose}>
      <form className="stack" onSubmit={run(() => api('PUT', `/sites/${site.siteId}/subscription`, { startedOn, renewsOn }))}>
        <div className="form-row">
          <label className="field">
            Started
            <input className="input" type="date" value={startedOn} onChange={(e) => setStarted(e.target.value)} required />
          </label>
          <label className="field">
            Renews
            <input className="input" type="date" value={renewsOn} onChange={(e) => setRenews(e.target.value)} required />
          </label>
        </div>
        {error && <p className="error">{error}</p>}
        <div className="row">
          <button className="btn primary" type="submit" disabled={busy}>
            Save
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function InvoiceDialog({ siteId, onClose, onDone }: { siteId: string; onClose: () => void; onDone: () => void }) {
  const [number, setNumber] = useState('');
  const [issuedOn, setIssuedOn] = useState(today());
  const [amount, setAmount] = useState('');
  const [status, setStatus] = useState<'paid' | 'unpaid'>('unpaid');
  const [file, setFile] = useState<File | null>(null);
  const { busy, error, run } = useSubmit(onDone, onClose);
  const submit = run(async () => {
    if (!file) throw new Error('Choose the invoice PDF.');
    if (file.size > 10 * 1024 * 1024) throw new Error('Invoices can be up to 10 MB.');
    const bytes = new Uint8Array(await file.arrayBuffer());
    await api('POST', `/sites/${siteId}/invoices`, { number, issuedOn, amount, status, fileName: file.name, fileBase64url: toB64u(bytes) });
  });
  return (
    <Dialog title="Upload an invoice" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <div className="form-row">
          <label className="field">
            Invoice number
            <input className="input" value={number} onChange={(e) => setNumber(e.target.value)} required autoFocus />
          </label>
          <label className="field">
            Date
            <input className="input" type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} required />
          </label>
          <label className="field">
            Amount
            <input className="input" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="£12,000" required />
          </label>
          <label className="field">
            Status
            <select className="input" value={status} onChange={(e) => setStatus(e.target.value as 'paid' | 'unpaid')}>
              <option value="unpaid">Unpaid</option>
              <option value="paid">Paid</option>
            </select>
          </label>
        </div>
        <label className="field">
          The invoice (PDF, up to 10 MB)
          <input className="input" style={{ paddingTop: 10 }} type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} required />
        </label>
        {error && <p className="error">{error}</p>}
        <div className="row">
          <button className="btn primary" type="submit" disabled={busy}>
            Upload
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function SwitchOffDialog({ site, onClose, onDone }: { site: SiteView; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('Not renewed');
  const [finalWarningOn, setWarning] = useState(today());
  const { busy, error, run } = useSubmit(onDone, onClose);
  return (
    <Dialog title={`Switch ${site.siteName} off`} onClose={onClose}>
      <form className="stack" onSubmit={run(() => api('POST', `/sites/${site.siteId}/switch-off`, { reason, finalWarningOn }))}>
        <div className="note red">
          The ops tablet will no longer be able to send alerts, and every phone will show that Z.A.S. is switched off at this site. It can be switched back on at any time.
        </div>
        <label className="field">
          Why
          <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} required />
        </label>
        <label className="field">
          When head office had its final warning
          <input className="input" type="date" value={finalWarningOn} onChange={(e) => setWarning(e.target.value)} required />
        </label>
        {error && <p className="error">{error}</p>}
        <div className="row">
          <button className="btn danger" type="submit" disabled={busy}>
            Switch it off
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}
